import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { UIGameActionButton } from '../../../common/UIGameActionButton/index.ts';
import { T } from '@colonist/ui';
import { ACTION_BUTTON_IDS, useActionButtonCommonErrors } from '../utils.ts';
import IconCheck from '@static/images/icon_check.svg';
import IconHourGlass from '@static/images/icon_hourglass.svg';
import IconPassTurn from '@static/images/icon_pass_turn.svg';
import { useHotkeys } from 'react-hotkeys-hook';
import { useGameStore } from '../../../store/GameStoreContext.ts';
import clsx from 'clsx';
import styles from '../UIGameActionButtonContainer.module.scss';
import { HiddenUIElement } from '@colonist/game-beginner-mode';
// eslint-disable-next-line boundaries/dependencies
import { StorageWrapper } from '../../../../../../code/client/shared/scripts/StorageWrapper.ts';
import { useEndTurnSuggestion } from './useEndTurnSuggestion.ts';
import { GameAction, ModeSettingEnum } from '@colonist/game-base-types';
import { useCurrentStateValidator } from '../../../store/selectors/useCurrentStateValidator.ts';
import { UIGameActionButtonTurnToggleCount } from './UIGameActionButtonTurnToggleCount.tsx';
const SPACEBAR_SHORTCUT_DISABLED_AT_TURN_START_MS = 500;
const getTurnButtonHelperText = ({ hasPlayerToggledEndTurn, disabled, errorHelperText })=>{
    if (hasPlayerToggledEndTurn) return {
        key: 'strings:game.errors.alreadyVotedToEndTurn'
    };
    if (disabled) return errorHelperText;
    return undefined;
};
export const UIGameActionButtonTurn = ({ className, hideTooltip = false })=>{
    const SPAM_PREVENTION_MS = 600;
    const [diceRolledTime, setDiceRolledTime] = useState(0);
    const lastClickTimeRef = useRef(0);
    const { sendClickedPassTurn } = useGameStore((state)=>state.gameSendEvent);
    const beginnerActionState = useGameStore((state)=>state.gameBeginnerHint.beginnerActionState);
    const diceState = useGameStore((state)=>state.gameState.diceState);
    const myColor = useGameStore((state)=>state.myColor);
    const { errorHelperText, hasCommonErrors } = useActionButtonCommonErrors();
    const isHidden = beginnerActionState === null || beginnerActionState === void 0 ? void 0 : beginnerActionState.hiddenUIElements.includes(HiddenUIElement.EndTurnActionButton);
    const isNotBeginnerPassedTurnAction = beginnerActionState != null && (beginnerActionState === null || beginnerActionState === void 0 ? void 0 : beginnerActionState.type) !== GameAction.PassedTurn;
    const currentStateValidator = useCurrentStateValidator();
    const hasPlayerToggledEndTurn = currentStateValidator.hasPlayerToggledEndTurn(myColor);
    const disabled = hasCommonErrors || isHidden || isNotBeginnerPassedTurnAction;
    const helperText = getTurnButtonHelperText({
        hasPlayerToggledEndTurn: hasPlayerToggledEndTurn,
        disabled: disabled,
        errorHelperText: errorHelperText
    });
    const { shouldShowSuggestion } = useEndTurnSuggestion();
    const setIsShowTradePanel = useGameStore((state)=>state.gameTrade.setIsShowTradePanel);
    const isColonistRush = useGameStore((state)=>state.gameSettings.modeSetting === ModeSettingEnum.ColonistRush4P);
    const sendRushGameEndTurnAnalytics = useGameStore((state)=>state.gameAnalytics.sendRushGameEndTurnAnalytics);
    const completedTurns = useGameStore((state)=>state.gameState.currentState.completedTurns);
    const onClick = useCallback(()=>{
        if (disabled) return;
        if (hasPlayerToggledEndTurn) return;
        const now = Date.now();
        const shouldPreventClick = now - lastClickTimeRef.current < SPAM_PREVENTION_MS;
        if (shouldPreventClick) return;
        lastClickTimeRef.current = now;
        if (isColonistRush) {
            sendRushGameEndTurnAnalytics(completedTurns + 1);
        }
        sendClickedPassTurn();
        setIsShowTradePanel(false);
    }, [
        disabled,
        hasPlayerToggledEndTurn,
        sendClickedPassTurn,
        setIsShowTradePanel,
        isColonistRush,
        completedTurns
    ]);
    useEffect(()=>{
        if (diceState.diceThrown) {
            setDiceRolledTime(Date.now());
        }
    }, [
        diceState.diceThrown
    ]);
    const isListeningForKeyPress = useCallback(()=>{
        if (!StorageWrapper.getKeyboardShortcuts()) return false;
        if (disabled) return false;
        const msSinceDiceRolled = Date.now() - diceRolledTime;
        if (msSinceDiceRolled < SPACEBAR_SHORTCUT_DISABLED_AT_TURN_START_MS) return false;
        return true;
    }, [
        disabled,
        diceRolledTime
    ]);
    useHotkeys('Space', ()=>{
        if (isListeningForKeyPress()) onClick === null || onClick === void 0 ? void 0 : onClick();
    }, [
        isListeningForKeyPress,
        onClick
    ]);
    const icon = useMemo(()=>{
        if (isColonistRush) return IconCheck;
        return disabled ? IconHourGlass : IconPassTurn;
    }, [
        isColonistRush,
        disabled
    ]);
    const soundOnClick = useMemo(()=>{
        if (isColonistRush) return undefined // TODO add custom sound for Colonist Rush
        ;
        return undefined;
    }, [
        isColonistRush
    ]);
    const isMechanicTurnColonistRushEnabled = useGameStore((state)=>{
        var _state_gameState_experimentalMechanicState;
        return ((_state_gameState_experimentalMechanicState = state.gameState.experimentalMechanicState) === null || _state_gameState_experimentalMechanicState === void 0 ? void 0 : _state_gameState_experimentalMechanicState.mechanicTurnColonistRushState) !== undefined;
    });
    const playerCount = isMechanicTurnColonistRushEnabled ? /*#__PURE__*/ React.createElement(UIGameActionButtonTurnToggleCount, null) : undefined;
    const tooltipTitle = useMemo(()=>{
        if (hideTooltip) return undefined;
        if (isMechanicTurnColonistRushEnabled) {
            return /*#__PURE__*/ React.createElement(T, {
                id: "strings:game.tooltips.endTurnRush"
            });
        }
        return /*#__PURE__*/ React.createElement(T, {
            id: "strings:game.tooltips.endTurn"
        });
    }, [
        hideTooltip,
        isMechanicTurnColonistRushEnabled
    ]);
    return /*#__PURE__*/ React.createElement(UIGameActionButton, {
        className: clsx(className, {
            [styles.hidden]: isHidden
        }),
        id: ACTION_BUTTON_IDS.passTurn,
        onClick: onClick,
        soundOnClick: soundOnClick,
        disabled: disabled,
        helperText: helperText,
        iconPath: icon,
        highlight: hasPlayerToggledEndTurn,
        tooltipTitle: tooltipTitle,
        scalePulseAnimation: shouldShowSuggestion,
        count: playerCount,
        countColor: myColor
    });
};
